// Validasi dan normalisasi konten JSON (config, kartu rumus, bank soal).
// Modul ini murni (tanpa DOM) supaya bisa dipakai di browser, skrip Node, dan tes.
import { richText } from '../core/richtext.js';
import { esc } from '../core/format.js';
import { ELEMEN_MATEMATIKA, elemenUntuk, TOPIK_PER_MAPEL } from '../subjects/topik.js';

// Elemen Matematika (kartu rumus). Elemen mapel lain: elemenUntuk(mapel).
export const ELEMEN = ELEMEN_MATEMATIKA;
export { elemenUntuk };
export const POLA_PATH = /^[a-z0-9][a-z0-9._-]*(\/[a-z0-9][a-z0-9._-]*)*\.(json|png|jpe?g|webp|svg)$/i;
export const pathAman = p => typeof p === 'string' && POLA_PATH.test(p) && !p.includes('..');

const isObj = v => v && typeof v === 'object' && !Array.isArray(v);
const isStr = v => typeof v === 'string' && v.trim() !== '';
const isNum = v => typeof v === 'number' && Number.isFinite(v);

// ---------- config.json ----------
export const KETERANGAN_NONAKTIF = /^\s*segera hadir\.?\s*$/i;

export function normalisasiConfig(raw, bawaan) {
  const galat = [];
  const cfg = structuredClone(bawaan);
  if (raw == null) return { config: cfg, galat };
  if (!isObj(raw)) return { config: cfg, galat: ['config: bukan objek JSON'] };

  const k = raw.kuis;
  if (isObj(k)) {
    if (isNum(k.batasLulus) && k.batasLulus >= 0 && k.batasLulus <= 100) cfg.kuis.batasLulus = k.batasLulus;
    else if (k.batasLulus !== undefined) galat.push('kuis.batasLulus harus angka 0–100');
    if (Array.isArray(k.pilihanJumlahSoal)) {
      const ok = k.pilihanJumlahSoal.filter(n => Number.isInteger(n) && n >= 1 && n <= 200);
      if (ok.length) cfg.kuis.pilihanJumlahSoal = ok;
      if (ok.length !== k.pilihanJumlahSoal.length) galat.push('kuis.pilihanJumlahSoal berisi nilai yang bukan bilangan bulat 1–200');
    }
    if (Number.isInteger(k.jumlahSoalAwal)) cfg.kuis.jumlahSoalAwal = k.jumlahSoalAwal;
    if (Number.isInteger(k.percobaanPerLangkah) && k.percobaanPerLangkah >= 1) cfg.kuis.percobaanPerLangkah = k.percobaanPerLangkah;
    if (typeof k.peringkat === 'boolean') cfg.kuis.peringkat = k.peringkat;
    else if (k.peringkat !== undefined) galat.push('kuis.peringkat harus true/false');
  }
  if (!cfg.kuis.pilihanJumlahSoal.includes(cfg.kuis.jumlahSoalAwal)) cfg.kuis.jumlahSoalAwal = cfg.kuis.pilihanJumlahSoal[0];

  if (Array.isArray(raw.mapel)) {
    const mapel = [];
    raw.mapel.forEach((m, i) => {
      if (!isObj(m) || !isStr(m.id) || !isStr(m.nama)) { galat.push(`mapel[${i}]: wajib punya id dan nama`); return; }
      const out = { id: m.id, nama: m.nama, aktif: m.aktif !== false, keterangan: isStr(m.keterangan) ? m.keterangan : '', simbol: isStr(m.simbol) ? m.simbol : m.nama.slice(0, 2) };
      // Mapel yang sudah diaktifkan tapi keterangannya masih "Segera hadir" (sisa saat belum aktif):
      // pakai keterangan bawaan supaya kartunya tidak terlihat belum tersedia.
      if (out.aktif && KETERANGAN_NONAKTIF.test(out.keterangan)) out.keterangan = bawaan.mapel?.find(x => x.id === m.id)?.keterangan || '';
      if (m.rumus !== undefined) { if (pathAman(m.rumus)) out.rumus = m.rumus; else galat.push(`mapel[${i}].rumus: path tidak valid`); }
      if (isObj(m.bankSoal)) {
        if (pathAman(m.bankSoal.file)) out.bankSoal = { file: m.bankSoal.file, porsi: isNum(m.bankSoal.porsi) ? Math.min(1, Math.max(0, m.bankSoal.porsi)) : 0.2 };
        else galat.push(`mapel[${i}].bankSoal.file: path tidak valid`);
      }
      if (isObj(m.bobotTopik)) {
        out.bobotTopik = {};
        for (const [t, w] of Object.entries(m.bobotTopik)) {
          if (isNum(w) && w >= 0) out.bobotTopik[t] = w; else galat.push(`mapel[${i}].bobotTopik.${t}: harus angka ≥ 0`);
        }
      }
      mapel.push(out);
    });
    if (mapel.length) cfg.mapel = mapel;
  }
  return { config: cfg, galat };
}

// ---------- kartu rumus ----------
export function normalisasiRumus(raw) {
  const galat = [], kartu = [];
  if (!isObj(raw) || !Array.isArray(raw.kartu)) return { kartu, galat: ['rumus: harus punya array "kartu"'] };
  const ids = new Set();
  raw.kartu.forEach((c, i) => {
    const at = `kartu[${i}]${c && c.id ? ' (' + c.id + ')' : ''}`;
    if (!isObj(c) || !isStr(c.id) || !isStr(c.judul) || !isStr(c.rumus)) { galat.push(`${at}: wajib punya id, judul, dan rumus`); return; }
    if (ids.has(c.id)) { galat.push(`${at}: id ganda`); return; }
    if (c.elemen !== undefined && !ELEMEN.includes(c.elemen)) galat.push(`${at}: elemen harus salah satu dari ${ELEMEN.join(', ')}`);
    ids.add(c.id);
    kartu.push({ id: c.id, elemen: ELEMEN.includes(c.elemen) ? c.elemen : '', judul: esc(c.judul), rumus: richText(c.rumus), keterangan: richText(c.keterangan || ''), contoh: richText(c.contoh || '') });
  });
  return { kartu, galat };
}

// ---------- bank soal ----------
const hurufKeIndeks = k => (typeof k === 'string' && /^[A-Fa-f]$/.test(k)) ? k.toUpperCase().charCodeAt(0) - 65 : (Number.isInteger(k) ? k : -1);
const rapat = t => String(t ?? '').replace(/\s+/g, ' ').trim();
// Paragraf teks bacaan dipisah baris kosong; baris tunggal (mis. larik puisi) tetap satu paragraf.
export const paragrafBacaan = teks => String(teks ?? '').replace(/\r\n?/g, '\n').split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
export const POLA_ID_BACAAN = /^[a-z0-9][a-z0-9-]{0,59}$/;
export const MAKS_TEKS_BACAAN = 6000;

function normalisasiLangkah(arr, at, galat) {
  if (arr === undefined) return [];
  if (!Array.isArray(arr)) { galat.push(`${at}.langkah: harus array`); return []; }
  const out = [];
  arr.forEach((s, j) => {
    const a = `${at}.langkah[${j}]`;
    if (!isObj(s)) { galat.push(`${a}: harus objek`); return; }
    const st = {};
    if (isStr(s.teks)) st.t = richText(s.teks);
    if (s.tanya !== undefined) {
      const t = s.tanya;
      if (isObj(t) && Array.isArray(t.pilihan)) {
        // Pertanyaan pilihan: anak mengetuk salah satu tombol.
        const k = hurufKeIndeks(t.jawaban);
        if (!isStr(t.soal) || t.pilihan.length < 2 || t.pilihan.length > 4 || !t.pilihan.every(isStr) || k < 0 || k >= t.pilihan.length) {
          galat.push(`${a}.tanya: pertanyaan pilihan wajib punya "soal", 2–4 "pilihan" (teks), dan "jawaban" berupa huruf pilihan`); return;
        }
        st.ask = { q: richText(t.soal), opsi: t.pilihan.map(richText), a: k };
      } else if (isObj(t) && isStr(t.soal) && isNum(t.jawaban)) st.ask = { q: richText(t.soal), a: t.jawaban, ...(isStr(t.satuan) ? { u: esc(t.satuan) } : {}) };
      else { galat.push(`${a}.tanya: wajib punya "soal" (teks) dan "jawaban" (angka), atau "pilihan" dan "jawaban" (huruf)`); return; }
    }
    if (isStr(s.setelah)) st.after = richText(s.setelah);
    if (!st.t && !st.ask) { galat.push(`${a}: isi "teks" atau "tanya"`); return; }
    out.push(st);
  });
  return out;
}

/** Validasi daftar teks bacaan (soal kelompok). Mengembalikan Map id -> bacaan. */
// Alamat gambar dari path R2: prefixGambar berupa awalan URL, atau fungsi path -> URL (mode demo).
export const urlGambar = (prefixGambar, p) => (typeof prefixGambar === 'function' ? prefixGambar(p) : prefixGambar + p);

export function normalisasiBacaan(arr, mapel, galat, prefixGambar = '/api/content/') {
  const peta = new Map();
  if (arr === undefined) return peta;
  if (!Array.isArray(arr)) { galat.push('"bacaan" harus array'); return peta; }
  const jenisSah = elemenUntuk(mapel);
  arr.forEach((b, i) => {
    const at = `bacaan[${i}]${b && b.id ? ' (' + b.id + ')' : ''}`;
    if (!isObj(b)) { galat.push(`${at}: harus objek`); return; }
    const g0 = galat.length;
    if (!isStr(b.id) || !POLA_ID_BACAAN.test(b.id)) galat.push(`${at}: "id" wajib, huruf kecil/angka/"-"`);
    else if (peta.has(b.id)) galat.push(`${at}: id ganda`);
    if (!jenisSah.includes(b.jenis)) galat.push(`${at}: "jenis" harus salah satu dari ${jenisSah.join(', ')}`);
    if (!isStr(b.judul)) galat.push(`${at}: wajib punya "judul"`);
    if (!isStr(b.teks)) galat.push(`${at}: wajib punya "teks"`);
    else if (b.teks.length > MAKS_TEKS_BACAAN) galat.push(`${at}: "teks" maksimal ${MAKS_TEKS_BACAAN} karakter`);
    if (b.bentuk !== undefined && !['prosa', 'puisi'].includes(b.bentuk)) galat.push(`${at}: "bentuk" harus "prosa" atau "puisi"`);
    if (b.sumber !== undefined && (!isStr(b.sumber) || b.sumber.length > 300)) galat.push(`${at}: "sumber" harus teks (maksimal 300 karakter)`);
    // Gambar teks (infografis, diagram): path R2, keterangan untuk pembaca layar, dan letaknya
    // (posisiGambar = nomor paragraf sebelum gambar; 0 = di atas teks).
    if (b.gambar !== undefined && !pathAman(b.gambar)) galat.push(`${at}: "gambar" harus path relatif di R2, misalnya "gambar/infografis.png"`);
    if (b.gambar !== undefined && !isStr(b.altGambar)) galat.push(`${at}: gambar wajib punya "altGambar" (keterangan isi gambar)`);
    if (b.posisiGambar !== undefined && !(Number.isInteger(b.posisiGambar) && b.posisiGambar >= 0)) galat.push(`${at}: "posisiGambar" harus bilangan bulat 0 atau lebih`);
    if (galat.length === g0) peta.set(b.id, {
      id: b.id, jenis: b.jenis, judul: rapat(b.judul), teks: paragrafBacaan(b.teks).join('\n\n'), bentuk: b.bentuk === 'puisi' ? 'puisi' : 'prosa',
      ...(isStr(b.sumber) ? { sumber: rapat(b.sumber) } : {}),
      ...(b.gambar !== undefined ? { gambar: { src: urlGambar(prefixGambar, b.gambar), alt: rapat(b.altGambar), posisi: b.posisiGambar || 0 } } : {}),
    });
  });
  return peta;
}

/**
 * @param {object} raw isi file bank soal ({ mapel?, bacaan?, soal })
 * @param {{prefixGambar?: string, mapel?: string}} opsi mapel menentukan elemen yang sah
 */
export function normalisasiBankSoal(raw, { prefixGambar = '/api/content/', mapel } = {}) {
  const galat = [], soal = [];
  if (!isObj(raw) || !Array.isArray(raw.soal)) return { soal, galat: ['bank soal: harus punya array "soal"'], bacaan: [] };
  const idMapel = mapel || (isStr(raw.mapel) ? raw.mapel : 'matematika');
  const elemenSah = elemenUntuk(idMapel), topikMapel = TOPIK_PER_MAPEL[idMapel] || {};
  const bacaan = normalisasiBacaan(raw.bacaan, idMapel, galat, prefixGambar);
  const ids = new Set();
  raw.soal.forEach((s, i) => {
    const at = `soal[${i}]${s && s.id ? ' (' + s.id + ')' : ''}`;
    const g0 = galat.length;
    if (!isObj(s)) { galat.push(`${at}: harus objek`); return; }
    if (!isStr(s.id)) galat.push(`${at}: wajib punya "id"`);
    else if (ids.has(s.id)) galat.push(`${at}: id ganda`);
    const b = s.bacaan === undefined ? null : bacaan.get(s.bacaan);
    if (s.bacaan !== undefined && !b) galat.push(`${at}: teks bacaan "${s.bacaan}" tidak ditemukan`);
    const elemen = s.elemen ?? b?.jenis;
    if (!elemenSah.includes(elemen)) galat.push(`${at}: "elemen" harus salah satu dari ${elemenSah.join(', ')}`);
    if (!isStr(s.teks)) galat.push(`${at}: wajib punya "teks"`);
    if (s.gambar !== undefined && !pathAman(s.gambar)) galat.push(`${at}: "gambar" harus path relatif di R2, misalnya "gambar/soal-1.png"`);
    const topik = isStr(s.topik) ? s.topik : 'lainnya', dikenal = topikMapel[topik];
    const q = {
      id: s.id, sumber: 'bank', topic: 'bank:' + topik, el: elemen,
      // Mapel tanpa generator (mis. Bahasa Indonesia): statistik dikelompokkan per topik.
      ...(dikenal && idMapel !== 'matematika' ? { gen: topik } : {}),
      title: esc(isStr(s.judul) ? s.judul : dikenal && idMapel !== 'matematika' ? dikenal.nama : 'Soal pilihan guru'), text: richText(s.teks),
      vis: isStr(s.gambar) && pathAman(s.gambar) ? `<img class="soal-img" src="${esc(urlGambar(prefixGambar, s.gambar))}" alt="${esc(s.altGambar || 'Gambar soal')}" loading="lazy">` : '',
      rumus: Array.isArray(s.rumus) ? s.rumus.filter(isStr) : [],
      ...(s.acakPilihan === false ? { acakPilihan: false } : {}),
      ...(b ? { bacaan: b } : {}),
    };
    const alasan = x => (isObj(x) && isStr(x.alasan) ? richText(x.alasan) : '');
    if (s.tipe === 'pg') {
      const p = s.pilihan;
      const teks = Array.isArray(p) ? p.map(x => (isObj(x) ? x.teks : x)) : null;
      if (!teks || teks.length < 2 || teks.length > 6 || !teks.every(isStr)) galat.push(`${at}: "pilihan" harus 2–6 teks (atau objek { teks, alasan })`);
      else if (new Set(teks).size !== teks.length) galat.push(`${at}: ada pilihan yang sama`);
      const k = hurufKeIndeks(s.kunci);
      if (!teks || k < 0 || k >= teks.length) galat.push(`${at}: "kunci" harus huruf pilihan (A, B, ...)`);
      Object.assign(q, { type: 'pg', options: teks ? teks.map(richText) : [], answer: k });
      const why = Array.isArray(p) ? p.map(alasan) : [];
      if (why.some(Boolean)) q.why = why;
    } else if (s.tipe === 'bs') {
      const p = s.pernyataan;
      if (!Array.isArray(p) || p.length < 1 || p.length > 6 || !p.every(x => isObj(x) && isStr(x.teks) && typeof x.benar === 'boolean')) galat.push(`${at}: "pernyataan" harus 1–6 objek { teks, benar: true/false }`);
      else Object.assign(q, { type: 'bs', statements: p.map(x => ({ t: richText(x.teks), v: x.benar, ...(alasan(x) ? { w: alasan(x) } : {}) })) });
      // Kategori pengganti "Benar"/"Salah", mis. ["Sesuai", "Tidak Sesuai"] (label untuk benar: true lalu benar: false).
      if (s.kategori !== undefined) {
        if (!Array.isArray(s.kategori) || s.kategori.length !== 2 || !s.kategori.every(isStr) || s.kategori[0].trim() === s.kategori[1].trim()) galat.push(`${at}: "kategori" harus dua label berbeda, mis. ["Sesuai", "Tidak Sesuai"]`);
        else q.labels = s.kategori.map(x => esc(x.trim()));
      }
    } else if (s.tipe === 'mcma') {
      const p = s.pilihan;
      if (!Array.isArray(p) || p.length < 2 || p.length > 6 || !p.every(x => isObj(x) && isStr(x.teks) && typeof x.benar === 'boolean')) galat.push(`${at}: "pilihan" harus 2–6 objek { teks, benar: true/false }`);
      else if (!p.some(x => x.benar)) galat.push(`${at}: minimal satu pilihan harus benar`);
      else Object.assign(q, { type: 'mcma', options: p.map(x => ({ t: richText(x.teks), v: x.benar, ...(alasan(x) ? { w: alasan(x) } : {}) })) });
    } else galat.push(`${at}: "tipe" harus "pg", "bs", atau "mcma"`);
    // Bukti: kutipan persis dari teks bacaan (disorot saat pembahasan). Harus ada di satu paragraf.
    if (s.bukti !== undefined) {
      if (!Array.isArray(s.bukti) || !s.bukti.every(isStr)) galat.push(`${at}: "bukti" harus daftar kutipan (teks)`);
      else if (!b) galat.push(`${at}: "bukti" hanya bisa dipakai pada soal yang punya teks bacaan`);
      else {
        const par = paragrafBacaan(b.teks).map(rapat);
        for (const k of s.bukti) if (!par.some(x => x.includes(rapat(k)))) galat.push(`${at}: kutipan bukti tidak ditemukan persis di satu paragraf teks bacaan: "${rapat(k).slice(0, 80)}"`);
        q.bukti = s.bukti.map(rapat);
      }
    }
    q.steps = normalisasiLangkah(s.langkah, at, galat);
    const adaPenjelasan = q.why || q.bukti?.length || (q.options || q.statements || []).some(x => x?.w);
    if (!q.steps.length && !adaPenjelasan) q.steps = [{ t: 'Baca soal pelan-pelan, lalu bandingkan dengan jawaban yang benar di bawah ini.' }];
    if (galat.length === g0) { ids.add(s.id); soal.push(q); }
  });
  return { soal, galat, bacaan: [...bacaan.values()] };
}
